<template>
  <div class="carousel" :class="{ 'carousel--single-slide': normalizedItems.length === 1 }">
    <!-- TODO: shared/loading が作成されたら共通化する -->
    <div v-if="loading" class="carousel__loading" aria-busy="true" aria-live="polite">
      <div class="carousel__loading-spinner" role="status" />
    </div>
    <p v-else-if="normalizedItems.length === 0" class="agent-recommend-carousel__empty">
      エージェントからのおすすめ求人はありません。
    </p>
    <div v-else ref="swiperRef" class="swiper">
      <div class="swiper-wrapper">
        <div v-for="(item, idx) in normalizedItems" :key="item.id || idx" class="swiper-slide">
          <div class="carousel__card-wrapper">
            <AgentRecommendJobCard :item="item" />
          </div>
        </div>
      </div>
      <div v-show="controlsVisible" class="carousel__controls">
        <button ref="prevRef" class="carousel__nav-btn" type="button" aria-label="前へ">
          <img :src="leftIcon" alt="前へ" />
        </button>
        <div class="carousel__dots">
          <div ref="paginationRef" class="swiper-pagination"></div>
        </div>
        <button ref="nextRef" class="carousel__nav-btn" type="button" aria-label="次へ">
          <img :src="rightIcon" alt="次へ" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import leftIcon from '@/assets/images/icons/carousel-left.svg';
import rightIcon from '@/assets/images/icons/carousel-right.svg';
import Swiper from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import AgentRecommendJobCard from './AgentRecommendJobCard.vue';
import type { AgentRecommendationItem } from './types';

/** カルーセル表示件数を切り替えるブレークポイント（px） */
const CAROUSEL_BREAKPOINT_SM = 640;
const CAROUSEL_BREAKPOINT_MD = 1247;

const debounce = (fn: (...args: unknown[]) => void, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  return (...args: unknown[]) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

const props = withDefaults(
  defineProps<{
    items: AgentRecommendationItem[];
    /** true のとき表示エリア内にローディングを表示する */
    loading?: boolean;
  }>(),
  { loading: false }
);

const swiperRef = ref<HTMLElement | null>(null);
const prevRef = ref<HTMLElement | null>(null);
const nextRef = ref<HTMLElement | null>(null);
const paginationRef = ref<HTMLElement | null>(null);
const swiperInstance = ref<Swiper | null>(null);
const controlsVisible = ref(true);

const normalizeSalary = (wageMin?: number | string, wageMax?: number | string) => {
  const toNum = (v?: number | string) =>
    v === undefined || v === null || v === '' ? undefined : Number(v);
  return { wageMin: toNum(wageMin), wageMax: toNum(wageMax) };
};

const normalizedItems = computed(() =>
  props.items.map((item) => {
    const { wageMin, wageMax } = normalizeSalary(item.job?.wageMin, item.job?.wageMax);
    return {
      ...item,
      comment: item.comment ?? '',
      job: {
        ...item.job,
        showNewChip: !!item.job?.showNewChip,
        prefectureNames: item.job?.prefectureNames?.filter(Boolean) || [],
        occupationDescriptions: item.job?.occupationDescriptions?.filter(Boolean) || [],
        industryNames: item.job?.industryNames?.filter(Boolean) || [],
        skills: item.job?.skills?.filter(Boolean) || [],
        wageMin,
        wageMax,
        showImageWrapper: item.job?.showImageWrapper ?? true,
      },
    };
  })
);

const slidesConfig = () => {
  const itemCount = normalizedItems.value.length;
  const clampSlides = (target: number) => Math.max(1, Math.min(target, itemCount || 1));

  const slidesPerViewForCurrentWidth = () => {
    const width = window.innerWidth;
    if (width >= CAROUSEL_BREAKPOINT_MD) return clampSlides(2);
    if (width >= CAROUSEL_BREAKPOINT_SM) return clampSlides(1);
    return clampSlides(1);
  };

  return {
    currentSlidesPerView: slidesPerViewForCurrentWidth(),
    base: {
      slidesPerView: clampSlides(1),
      spaceBetween: 10,
    },
    breakpoints: {
      [CAROUSEL_BREAKPOINT_SM]: {
        slidesPerView: clampSlides(1),
        spaceBetween: 12,
        centeredSlides: itemCount === 1,
      },
      [CAROUSEL_BREAKPOINT_MD]: {
        slidesPerView: clampSlides(2),
        spaceBetween: 16,
        centeredSlides: itemCount === 1,
      },
    },
  };
};

const initSwiper = () => {
  if (!swiperRef.value) return;
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true);
  }

  const { currentSlidesPerView, base, breakpoints } = slidesConfig();
  const itemCount = normalizedItems.value.length;
  const canSlide = itemCount > currentSlidesPerView;
  controlsVisible.value = canSlide;
  /** 1枚のみのときは中央に配置 */
  const centeredSlides = itemCount === 1;

  swiperInstance.value = new Swiper(swiperRef.value, {
    modules: [Navigation, Pagination, Autoplay],
    ...base,
    loop: canSlide,
    allowTouchMove: canSlide,
    centeredSlides,
    navigation: {
      nextEl: nextRef.value,
      prevEl: prevRef.value,
      enabled: canSlide,
    },
    pagination: {
      el: paginationRef.value,
      clickable: canSlide,
      enabled: canSlide,
    },
    breakpoints,
  });
};

const debouncedInitSwiper = debounce(initSwiper, 250);

onMounted(() => {
  if (!props.loading) initSwiper();
  window.addEventListener('resize', debouncedInitSwiper);
});

watch(
  () => props.loading,
  (isLoading) => {
    if (!isLoading) nextTick(initSwiper);
  }
);

onBeforeUnmount(() => {
  window.removeEventListener('resize', debouncedInitSwiper);
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true);
  }
});
</script>

<style scoped lang="scss">
@import '@/assets/stylesheets/components/_variables.scss';

.carousel {
  width: 100%;
  max-width: 1150px;
  margin: 0 auto;
}

.swiper {
  width: 100%;
  padding: 4px;
  position: relative;
}

/* 1枚のみのときはカードを中央に配置（Swiperの固定幅をやめ、autoで中央寄せ） */
.carousel--single-slide :deep(.swiper-wrapper) {
  justify-content: center;
}

.carousel--single-slide :deep(.swiper-slide) {
  width: auto !important;
}

.swiper-slide {
  height: auto;
  display: flex;
  min-width: 0;
}

.carousel__card-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
  min-width: 0;
}

.carousel__controls {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.carousel__nav-btn {
  width: 32px;
  height: 32px;
  border: none;
  padding: 0;
  border-radius: 100px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.carousel__nav-btn img {
  width: 24px;
  height: 24px;
}

.carousel__dots {
  display: flex;
  align-items: center;
}

:deep(.swiper-pagination) {
  position: static;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

:deep(.swiper-pagination-bullet) {
  width: 10px;
  height: 10px;
  background: #d9d9d9;
  opacity: 1;
}

:deep(.swiper-pagination-bullet-active) {
  background: #0699d6;
}

.carousel__nav-btn:hover img {
  opacity: 0.8;
}

.agent-recommend-carousel__empty {
  margin: 0;
  padding: 24px 16px;
  text-align: center;
  color: #545454;
  font-size: 14px;
  line-height: 1.5;
}

/* TODO: shared/loading が作成されたら共通化する */
.carousel__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
  width: 100%;
}

/* コミット fa4ff34 の Loading.vue と統一したスピナーデザイン（Bootstrap .spinner-border 相当） */
.carousel__loading-spinner {
  display: inline-block;
  width: 5rem;
  height: 5rem;
  vertical-align: -0.125em;
  border: 0.4em solid currentcolor;
  border-right-color: transparent;
  border-radius: 50%;
  color: $primary-blue;
  animation: carousel-spinner-rotate 0.75s linear infinite;
}

@keyframes carousel-spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}
</style>
